import React, { useState, useEffect } from 'react';
import LoginPage from './components/LoginPage';
import MenuPage from './components/MenuPage';
import InventoryPage from './components/InventoryPage'; // 추가
import { ethers } from 'ethers';
import './styles/securityModal.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isInventory, setIsInventory] = useState(false);
  const [account, setAccount] = useState(null);
  const [inventoryNFTs, setInventoryNFTs] = useState([]);
  const [walletNFTs, setWalletNFTs] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [securityPrompt, setSecurityPrompt] = useState(null);

  const CONTRACT_ADDRESS = '0x674CF76e13945916b585ee293CBc2731607B953d';
  const INVENTORY_ADDRESS = '0xf3234A0123b50eBc2E440537c6F329a7196ba89d';
  const CONTRACT_ABI = [
    "function tokenURI(uint256 tokenId) view returns (string)",
    "function transferFrom(address from, address to, uint256 tokenId)"
  ];
  const API_BASE_URL = 'https://api.adena-fi.com';

  const isAutoLogoutEnabled = false; // 새로고침 로그아웃 기능 활성화 여부 설정

  useEffect(() => {
    const savedLoginState = localStorage.getItem('isLoggedIn');
    const savedAccount = localStorage.getItem('account');

    // 새로고침 시 자동 로그아웃 처리 (isAutoLogoutEnabled가 true일 경우에만)
    if (isAutoLogoutEnabled && savedLoginState === 'true' && savedAccount) {
      alert("You have been logged out due to a reload.");
      handleLogout();
    }
  }, []);

  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          setAccount(null);
          setWalletNFTs([]);
          setInventoryNFTs([]);
          localStorage.removeItem('account');
          alert("Your wallet has been disconnected. Please connect the linked wallet again.");
          return;
        }

        const newAccount = normalizeWalletAddress(accounts[0]);
        const currentAccount = account ? normalizeWalletAddress(account) : null;

        if (currentAccount && newAccount === currentAccount) {
          return;
        }

        setAccount(null);
        setWalletNFTs([]);
        setInventoryNFTs([]);
        localStorage.removeItem('account');
        alert("MetaMask account changed. Please connect the wallet linked to this email account again.");
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, [account]);

  useEffect(() => {
    // 인벤토리 페이지로 이동 시 fetchNFTs 호출
    if (isInventory && account) {
      console.log("Go to the Inventory page. Start importing NFT data.");
      fetchNFTs(true, account, setInventoryNFTs, setLoadingInventory);
      fetchNFTs(false, account, setWalletNFTs, setLoadingWallet);
    }
  }, [isInventory, account]);

  const handleLogin = (connectedAccount) => {
    setIsLoggedIn(true);
    setAccount(connectedAccount);
    setIsInventory(false); // 기본 메뉴 페이지로 설정
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('account', connectedAccount);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setIsInventory(false);
    setAccount(null);
    setWalletNFTs([]);
    setInventoryNFTs([]);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('account');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('authToken');
    alert("Logout successful.");
  };

  const normalizeWalletAddress = (walletAddress) => {
    return ethers.utils.getAddress(walletAddress).toLowerCase();
  };

  const maskWalletAddress = (walletAddress) => {
    if (!walletAddress) return '';
    return `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Log in is required.');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const readApiError = async (response) => {
    try {
      const data = await response.json();
      return data.message || data.error || data.code || response.statusText;
    } catch (error) {
      return response.statusText || 'Request failed.';
    }
  };

  const apiGet = async (path) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await readApiError(response));
    }

    return response.json();
  };

  const apiPost = async (path, body) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await readApiError(response));
    }

    return response.json();
  };

  const getWalletStatus = async () => {
    return apiGet('/wallet/status');
  };

  const requestSecurityConfirmation = ({ title, walletAddress, tokenId, bodyLines, confirmText }) => {
    return new Promise((resolve) => {
      setSecurityPrompt({
        title,
        walletAddress,
        tokenId,
        bodyLines,
        confirmText: confirmText || 'Confirm',
        resolve,
      });
    });
  };

  const closeSecurityPrompt = (confirmed) => {
    if (securityPrompt && securityPrompt.resolve) {
      securityPrompt.resolve(confirmed);
    }
    setSecurityPrompt(null);
  };

  const signWalletMessage = async (message) => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    return signer.signMessage(message);
  };

  const verifyWalletOwnership = async (walletAddress) => {
    const nonceData = await apiPost('/wallet/nonce', {
      walletAddress,
      purpose: 'wallet-connect',
    });
    const signature = await signWalletMessage(nonceData.message);

    return apiPost('/wallet/verify', {
      walletAddress,
      nonce: nonceData.nonce,
      signature,
    });
  };

  const ensureWalletReady = async (walletAddress) => {
    const normalizedWallet = normalizeWalletAddress(walletAddress);
    const status = await getWalletStatus();
    const linkedWallet = status.walletAddress ? normalizeWalletAddress(status.walletAddress) : null;

    if (linkedWallet && linkedWallet !== normalizedWallet) {
      throw new Error(`This email account is already linked to ${maskWalletAddress(linkedWallet)}. Switch MetaMask to the linked wallet or contact support.`);
    }

    if (linkedWallet && status.verified) {
      return status;
    }

    const userEmail = localStorage.getItem('userEmail') || 'this email account';
    const confirmed = await requestSecurityConfirmation({
      title: linkedWallet ? 'Verify Wallet' : 'Link Wallet',
      walletAddress: normalizedWallet,
      bodyLines: [
        `Email: ${userEmail}`,
        linkedWallet
          ? 'Sign once to verify ownership of the wallet already linked to this account.'
          : 'This wallet will be linked to this email account.',
        'To change the linked wallet later, please contact support.',
        'Choose the wallet that owns your NFT or will claim your NFT.',
      ],
      confirmText: 'Confirm',
    });

    if (!confirmed) {
      throw new Error('Wallet connection canceled.');
    }

    await verifyWalletOwnership(normalizedWallet);
    return getWalletStatus();
  };

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        const connectedWalletAddress = normalizeWalletAddress(accounts[0]);

        await ensureWalletReady(connectedWalletAddress);
  
        // 연결 성공 시 NFT 데이터 갱신
        handleLogin(connectedWalletAddress);
        await Promise.all([
          fetchNFTs(false, connectedWalletAddress, setWalletNFTs, setLoadingWallet),
          fetchNFTs(true, connectedWalletAddress, setInventoryNFTs, setLoadingInventory),
        ]);
      } catch (err) {
        console.error("Wallet connection error:", err.message);
        alert(`Wallet connection failed: ${err.message}`);
      }
    } else {
      alert("Install the Metamask and set up the BSC network.");
    }
  };

  const fetchNFTs = async (isServerInventory, address, setNFTs, setLoading) => {
    setLoading(true);
    try {
      const endpoint = isServerInventory
        ? `${API_BASE_URL}/nft/tokens/inventory/${address}`
        : `${API_BASE_URL}/nft/tokens/address/${address}`;

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`API failed: ${response.statusText}`);

      const data = await response.json();
      if (!data.tokens || !Array.isArray(data.tokens)) {
        throw new Error("Invalid data type: field 'tokens' does not exist or is not an array.");
      }

      const tokenIds = data.tokens;

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);

      const nftPromises = tokenIds.map(async (tokenId) => {
        try {
          const tokenURI = await contract.tokenURI(tokenId);
          const metadata = await fetch(tokenURI).then((res) => res.json());
          return {
            tokenId: tokenId.toString(),
            image: metadata.image || '',
            name: metadata.name || `Token ${tokenId}`,
          };
        } catch (err) {
          console.error(`Failed to load NFT metadata: ${tokenId}`, err);
          return null;
        }
      });

      const results = await Promise.all(nftPromises);
      setNFTs(results.filter((nft) => nft !== null));
    } catch (err) {
      console.error("NFT lookup error:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const transferNFT = async (tokenId, toAddress) => {
    setTransferring(true);
    try {
      await ensureWalletReady(account);

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);

      const tx = await contract.transferFrom(account, toAddress, tokenId);
      await tx.wait();

      fetchNFTs(false, account, setWalletNFTs, setLoadingWallet);
      fetchNFTs(true, account, setInventoryNFTs, setLoadingInventory);

      alert(`NFT transfer Successful! Transaction Hash: ${tx.hash}`);
    } catch (err) {
      console.error("NFT transfer Failed:", err.message);
      alert(`NFT transfer Failed: ${err.message}`);
    }
    setTransferring(false);
  };

  const getBackNFT = async (tokenId) => {
    setTransferring(true);
    try {
      const normalizedWallet = normalizeWalletAddress(account);
      await ensureWalletReady(normalizedWallet);

      const confirmed = await requestSecurityConfirmation({
        title: 'Claim NFT',
        walletAddress: normalizedWallet,
        tokenId,
        bodyLines: [
          `Adena #${tokenId} will be claimed to your verified wallet.`,
          'MetaMask signature is required before the transfer starts.',
        ],
        confirmText: 'Confirm',
      });

      if (!confirmed) {
        throw new Error('NFT claim canceled.');
      }

      const nonceData = await apiPost('/wallet/nonce', {
        walletAddress: normalizedWallet,
        purpose: 'nft-withdraw',
        tokenId,
      });
      const signature = await signWalletMessage(nonceData.message);
      const response = await apiPost('/nft/tokens/withdraw-secure', {
        tokenId,
        walletAddress: normalizedWallet,
        nonce: nonceData.nonce,
        signature,
      });

      alert(`Your NFT has been successfully restored. Transaction Hash: ${response.transactionHash}`);

      fetchNFTs(false, account, setWalletNFTs, setLoadingWallet);
      fetchNFTs(true, account, setInventoryNFTs, setLoadingInventory);
    } catch (err) {
      console.error('NFT Recovery Failed:', err.message);
      alert(`NFT Recovery Failed: ${err.message}`);
    }
    setTransferring(false);
  };

  const refreshNFTs = async () => {
    if (account) {
      console.log("NFT Data Update Start...");
      await Promise.all([
        fetchNFTs(true, account, setInventoryNFTs, setLoadingInventory),
        fetchNFTs(false, account, setWalletNFTs, setLoadingWallet),
      ]);
      console.log("NFT data update completed.");
    } else {
      alert("Your wallet is not connected.");
    }
  };

  const renderSecurityPrompt = () => {
    if (!securityPrompt) return null;

    return (
      <div className="security-modalOverlay" role="dialog" aria-modal="true">
        <div className="security-modalPanel">
          <h2>{securityPrompt.title}</h2>
          <div className="security-walletBox">
            {maskWalletAddress(securityPrompt.walletAddress)}
          </div>
          {securityPrompt.tokenId && (
            <p className="security-tokenText">Adena #{securityPrompt.tokenId}</p>
          )}
          <div className="security-copy">
            {securityPrompt.bodyLines.map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
          <div className="security-actions">
            <button className="security-secondaryButton" onClick={() => closeSecurityPrompt(false)}>
              Cancel
            </button>
            <button className="security-primaryButton" onClick={() => closeSecurityPrompt(true)}>
              {securityPrompt.confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  };

  let page;

  if (!isLoggedIn) {
    page = <LoginPage onLogin={connectWallet} />;
  } else if (!isInventory) {
    page = (
      <MenuPage 
      onNavigateInventory={() => setIsInventory(true)} 
      handleLogout={handleLogout}
      />
    );
  } else {
    page = (
      <InventoryPage
        account={account}
        inventoryNFTs={inventoryNFTs}
        walletNFTs={walletNFTs}
        loadingInventory={loadingInventory}
        loadingWallet={loadingWallet}
        transferring={transferring}
        handleLogout={handleLogout}
        connectWallet={connectWallet}
        getBackNFT={getBackNFT}
        transferNFT={transferNFT}
        INVENTORY_ADDRESS={INVENTORY_ADDRESS}
        refreshNFTs={refreshNFTs} // 갱신 함수 전달
      />
    );
  }
  
  return (
    <>
      {page}
      {renderSecurityPrompt()}
    </>
  );
}

export default App;
